import React, { useMemo, useState } from "react";
import { createEliminationMatches, deleteEliminationMatches } from "services/matchService";
import * as Sentry from "@sentry/react";

const DualDivisionEliminationManager = ({ tournament, matches, reloadMatches }) => {
  const [bracketSizeDiv1, setBracketSizeDiv1] = useState(4);
  const [bracketSizeDiv2, setBracketSizeDiv2] = useState(4);
  const [loadingDivision, setLoadingDivision] = useState(null);

  const division1Teams = useMemo(() => tournament?.teams?.division1?.length || 0, [tournament]);
  const division2Teams = useMemo(() => tournament?.teams?.division2?.length || 0, [tournament]);

  const eliminationMatches = useMemo(() => matches?.filter((m) => m.phase === "elimination") || [], [matches]);

  const hasEliminationStarted = useMemo(() => ({
    division1: eliminationMatches.some((m) => m.division === "division1"),
    division2: eliminationMatches.some((m) => m.division === "division2"),
  }), [eliminationMatches]);

  const getAvailableBracketSizes = (teamsCount) => {
    const options = [];
    if (teamsCount >= 2) options.push(2);
    if (teamsCount >= 3) options.push(4);
    if (teamsCount >= 5) options.push(8);
    if (teamsCount >= 9) options.push(16);
    if (teamsCount >= 17) options.push(32);
    if (teamsCount >= 33) options.push(64);
    return options;
  };

  const handleStartElimination = async (division, bracketSize) => {
    setLoadingDivision(division);
    try {
      await createEliminationMatches({
        tournamentId: tournament.id,
        divisionName: division,
        bracketSize,
      });
      reloadMatches();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setLoadingDivision(null);
    }
  };

  const handleCancelElimination = async (division) => {
    const confirm = window.confirm("Êtes-vous sûr de vouloir annuler cette phase éliminatoire ?");
    if (!confirm) return;

    setLoadingDivision(division);
    try {
      await deleteEliminationMatches({
        tournamentId: tournament.id,
        divisionName: division,
      });
      reloadMatches();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setLoadingDivision(null);
    }
  };

  return (
    <div className="w-full flex flex-col items-center bg-slate-800 p-6 space-y-6">
      <h2 className="text-2xl font-bold text-slate-50">Éliminatoires séparés</h2>

      {/* Division 1 */}
      <div className="text-slate-50 flex flex-col items-center gap-2">
        <p className="font-bold">Division 1</p>
        {!hasEliminationStarted.division1 && (
          <>
            <label>Taille du bracket :</label>
            <select
              value={bracketSizeDiv1}
              onChange={(e) => setBracketSizeDiv1(Number(e.target.value))}
              className="rounded-md px-4 py-2 text-black text-sm font-bold"
            >
              {getAvailableBracketSizes(division1Teams).map((size) => (
                <option key={size} value={size}>
                  {size} équipes
                </option>
              ))}
            </select>
          </>
        )}
        {hasEliminationStarted.division1 ? (
          <button
            onClick={() => handleCancelElimination("division1")}
            disabled={loadingDivision === "division1"}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg"
          >
            {loadingDivision === "division1" ? "Annulation..." : "Annuler Division 1"}
          </button>
        ) : (
          <button
            onClick={() => handleStartElimination("division1", bracketSizeDiv1)}
            disabled={loadingDivision === "division1"}
            className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg"
          >
            {loadingDivision === "division1" ? "Démarrage..." : "Démarrer Division 1"}
          </button>
        )}
      </div>

      {/* Division 2 */}
      <div className="text-slate-50 flex flex-col items-center gap-2">
        <p className="font-bold">Division 2</p>
        {!hasEliminationStarted.division2 && (
          <>
            <label>Taille du bracket :</label>
            <select
              value={bracketSizeDiv2}
              onChange={(e) => setBracketSizeDiv2(Number(e.target.value))}
              className="rounded-md px-4 py-2 text-black text-sm font-bold"
            >
              {getAvailableBracketSizes(division2Teams).map((size) => (
                <option key={size} value={size}>
                  {size} équipes
                </option>
              ))}
            </select>
          </>
        )}
        {hasEliminationStarted.division2 ? (
          <button
            onClick={() => handleCancelElimination("division2")}
            disabled={loadingDivision === "division2"}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg"
          >
            {loadingDivision === "division2" ? "Annulation..." : "Annuler Division 2"}
          </button>
        ) : (
          <button
            onClick={() => handleStartElimination("division2", bracketSizeDiv2)}
            disabled={loadingDivision === "division2"}
            className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg"
          >
            {loadingDivision === "division2" ? "Démarrage..." : "Démarrer Division 2"}
          </button>
        )}
      </div>
    </div>
  );
};

export default DualDivisionEliminationManager;
